/**
 * `useApexOverlay` — for a manual-test parameter (e.g. "Alkalinity"),
 * discover the matching live Apex probe (incl. Trident alk/ca/mg) and
 * fetch its /api/timeseries history for the given window.
 *
 * Returns empty points (no error UI) when the agent is unreachable or
 * the parameter has no Apex counterpart — the overlay is best-effort.
 */
import { useEffect, useState } from 'react';

import { devApi } from '../lib/dev-api';
import { resolveProbeId, type ProbeId } from '../lib/probe-thresholds';
import { fetchTimeSeries, type TimeSeriesPoint } from '../lib/timeseries-api';

/** Manual-test parameter name (lowercased) → canonical Apex probe id. */
const PARAMETER_TO_PROBE: Record<string, ProbeId> = {
  alkalinity: 'alk',
  calcium: 'ca',
  magnesium: 'mg',
  ph: 'pH',
  temperature: 'tmp',
  orp: 'orp',
};

export function probeIdForParameter(parameter: string): ProbeId | null {
  return PARAMETER_TO_PROBE[parameter.trim().toLowerCase()] ?? null;
}

export interface ApexOverlayState {
  /** Empty when no Apex match / agent unreachable. */
  points: TimeSeriesPoint[];
  /** Canonical probe id when the parameter maps, else null. */
  probeId: ProbeId | null;
  loading: boolean;
}

export function useApexOverlay(
  parameter: string | null,
  fromMs: number,
  toMs: number,
): ApexOverlayState {
  const probeId = parameter ? probeIdForParameter(parameter) : null;
  const [state, setState] = useState<ApexOverlayState>({
    points: [],
    probeId,
    loading: probeId !== null,
  });

  useEffect(() => {
    let cancelled = false;
    if (!probeId) {
      setState({ points: [], probeId: null, loading: false });
      return;
    }
    setState({ points: [], probeId, loading: true });
    void (async () => {
      // 1. Discover the raw Apex probe name from the live snapshot.
      const stateRes = await devApi.state();
      if (cancelled) return;
      if (!stateRes.ok) {
        setState({ points: [], probeId, loading: false });
        return;
      }
      const probes = stateRes.value.apex?.probes ?? [];
      const match = probes.find((p) => resolveProbeId(p.name) === probeId);
      if (!match) {
        setState({ points: [], probeId, loading: false });
        return;
      }
      // 2. Fetch the history window for that probe.
      const seriesRes = await fetchTimeSeries({
        deviceId: 'apex',
        sensorId: match.name,
        from: fromMs,
        to: toMs,
        maxPoints: 300,
      });
      if (cancelled) return;
      setState({
        points: seriesRes.ok ? seriesRes.value : [],
        probeId,
        loading: false,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [probeId, fromMs, toMs]);

  return state;
}
